/**
 * battle-templates.ts — v0M4.3.2 (2026-07-23).
 *
 * Typed loader for the terrain templates emitted from BattleMap.xlsx by
 * `scripts/convert-xlsx.ts` into `src/data/generated/battle-map.json`.
 *
 * Consumers (v0M4.3.3+):
 *  - `battle-factory.ts::pickTerrainTemplate` — selects a template based
 *    on battle-create parameters (settlement_walled / settlement_nowalls / river)
 *    and falls back to the procedural v0M4.0j.1 generator when no
 *    matching template exists.
 *
 * Cell token legend (Michael's BattleMap.xlsx):
 *   X   passable ground (default = strategic-map terrain cue at battle-create)
 *   O   city interior tile
 *   OO  wall (impassable; HP per wall_hp dial)
 *   OOO gate (walkable; visually distinct from passable ground)
 *   =   river (impassable to non-cavalry; slow for cavalry)
 *   |   bridge (walkable, spans river)
 *
 * Any other cell content is treated as `X` (passable) — future-proofs
 * against xlsx typos and unrecognized legend expansions.
 */

import { assertSchemaVersion, BATTLE_MAP_SCHEMA_VERSION } from './loader';
import rawBlob from './generated/battle-map.json';

/**
 * Category of a terrain template. Drives template selection in
 * `battle-factory.ts` — the sim looks up templates by category and
 * RNG-picks among multiple candidates (e.g. 3 RiverCrossing variants).
 */
/**
 * v0M7.4d+siege (2026-07-26) — 'fort_walled' added to admit
 * Fort-assault templates authored by Michael in BattleMap.xlsx.
 * Consumed by `battle-factory.ts::createFortAssaultBattleFromArmies`.
 */
export type TerrainTemplateCategory = 'settlement_walled' | 'settlement_nowalls' | 'river' | 'fort_walled';

export interface TerrainTemplate {
  readonly name: string;
  readonly category: TerrainTemplateCategory;
  /**
   * Row-major grid of raw cell tokens from the xlsx (post-ingestion).
   *
   * - `settlement_walled` / `settlement_nowalls` / `fort_walled`: **20 cols × 20 rows**
   *   (full battlescape; siege geometry is asymmetric attacker-vs-walls).
   * - `river`: **20 cols × 10 rows** (v0M13.2a half-template; the
   *   convert-xlsx pipeline auto-splits each authored 20×20 sheet into
   *   `{sheetName}_top` (rows 0-9) + `{sheetName}_bot` (rows 10-19), both
   *   in the same category pool). Runtime (battle-factory.ts) picks two
   *   halves per battle and stitches them, mirroring the top half so the
   *   player always occupies rows 10-19 of the final 20×20 battlescape.
   */
  readonly grid: readonly (readonly string[])[];
}

export type TerrainTemplates = Readonly<Record<string, TerrainTemplate>>;

/**
 * Load the terrain-template dictionary. Asserts schema version + basic
 * shape sanity.
 */
export function loadTerrainTemplates(): TerrainTemplates {
  assertSchemaVersion(rawBlob, BATTLE_MAP_SCHEMA_VERSION, 'src/data/generated/battle-map.json');
  const blob = rawBlob as unknown as { terrainTemplates: TerrainTemplates };
  return blob.terrainTemplates;
}

/**
 * List all templates in a given category, sorted by name for
 * deterministic iteration order.
 */
export function templatesByCategory(
  templates: TerrainTemplates,
  category: TerrainTemplateCategory,
): TerrainTemplate[] {
  const out: TerrainTemplate[] = [];
  for (const name of Object.keys(templates).sort()) {
    const t = templates[name]!;
    if (t.category === category) out.push(t);
  }
  return out;
}
